import { PROFILE_BIO_MAX_LENGTH } from '@/features/profile/edit/constants';
import type { ProfileEditFormValues } from '@/features/profile/edit/types';
import type { ProfileGender } from '@/features/profile/edit/types';
import { getInitials } from '@/lib/getInitials';

export function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const trimmed = fullName.trim();
  if (!trimmed) {
    return { firstName: '', lastName: '' };
  }
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

export function buildFullName(firstName: string, lastName: string): string {
  return [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
}

export function profileEditToDisplayName(
  values: Pick<ProfileEditFormValues, 'firstName' | 'lastName'>,
): string {
  return buildFullName(values.firstName, values.lastName);
}

export function profileEditToInitials(
  values: Pick<ProfileEditFormValues, 'firstName' | 'lastName'>,
): string {
  return getInitials(profileEditToDisplayName(values));
}

/** The database gender enum accepts only these two values; every other UI option clears it. */
export function serializeProfileGender(gender: ProfileGender): 'Male' | 'Female' | null {
  if (gender === 'male') return 'Male';
  if (gender === 'female') return 'Female';
  return null;
}

export interface ProfileEditValidationErrors {
  firstName?: string;
  lastName?: string;
  username?: string;
  institution?: string;
  bio?: string;
  email?: string;
  mobile?: string;
  dateOfBirth?: string;
}

export function validateProfileEdit(
  values: ProfileEditFormValues,
  locks: { firstName: boolean; lastName: boolean; username: boolean; institution: boolean },
): ProfileEditValidationErrors {
  const errors: ProfileEditValidationErrors = {};

  if (locks.firstName && !values.firstName.trim()) {
    errors.firstName = 'First name is required.';
  }
  if (locks.lastName && !values.lastName.trim()) {
    errors.lastName = 'Last name is required.';
  }
  if (locks.username) {
    const username = values.username.trim();
    if (!username) {
      errors.username = 'Username is required.';
    } else if (!/^[A-Za-z0-9_]{3,30}$/.test(username)) {
      errors.username = 'Use 3–30 letters, numbers, or underscores.';
    }
  }
  if (locks.institution && !values.institution.trim()) {
    errors.institution = 'Institution is required.';
  }
  if (values.bio.length > PROFILE_BIO_MAX_LENGTH) {
    errors.bio = `Bio must be ${PROFILE_BIO_MAX_LENGTH} characters or fewer.`;
  }
  if (!values.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }
  if (values.mobile.trim() && !/^[\d\s+().-]{7,20}$/.test(values.mobile.trim())) {
    errors.mobile = 'Enter a valid mobile number.';
  }
  if (values.dateOfBirth && Number.isNaN(Date.parse(values.dateOfBirth))) {
    errors.dateOfBirth = 'Enter a valid date of birth.';
  }

  return errors;
}

export function hasValidationErrors(errors: ProfileEditValidationErrors): boolean {
  return Object.keys(errors).length > 0;
}
